import { useState, useEffect, useCallback } from 'react';
import { authorizedFetch } from '../../../../utils/api';

const AUTH_BASE_URL = import.meta.env.VITE_AUTH_BASE_URL || 'https://api2.simplize.vn';

export const WATCHLIST_UPDATED_EVENT = 'watchlist-updated';

export function useWatchlistSidebarData() {
    const [state, setState] = useState({ items: null, id: null, loading: true, notFound: false, list: [] });
    const [tick, setTick] = useState(0);
    const [activeGuid, setActiveGuid] = useState(null);

    useEffect(() => {
        const handler = () => setTick((t) => t + 1);
        window.addEventListener(WATCHLIST_UPDATED_EVENT, handler);
        return () => window.removeEventListener(WATCHLIST_UPDATED_EVENT, handler);
    }, []);

    useEffect(() => {
        let cancelled = false;
        async function load() {
            try {
                const listRes = await authorizedFetch(`${AUTH_BASE_URL}/api/personalize/watchlist/list`);
                const list = listRes?.data;
                if (!Array.isArray(list) || list.length === 0) {
                    if (!cancelled) setState({ items: null, id: null, loading: false, notFound: true, list: [] });
                    return;
                }
                const target = list.find((w) => w.guid === activeGuid) || list.find((w) => w.isDefault) || list[0];
                const detailRes = await authorizedFetch(
                    `${AUTH_BASE_URL}/api/personalize/watchlist/detail-public/v2/${target.guid}`
                );
                const detail = detailRes?.data;
                const rawItems = Array.isArray(detail?.items) ? detail.items.filter((i) => i.type === 'stock') : [];
                const items = rawItems.map((item) => ({
                    canonical_id: item.id,
                    symbol: item.ticker,
                    price: item.indicators?.priceClose ?? null,
                    change_pct: item.indicators?.pctChange ?? null,
                    report_count: 0,
                }));
                if (!cancelled) setState({ items, id: target.guid, name: target.name, loading: false, notFound: false, list });
            } catch {
                if (!cancelled) setState({ items: null, id: null, loading: false, notFound: true, list: [] });
            }
        }
        load();
        return () => { cancelled = true; };
    }, [tick, activeGuid]);

    const switchWatchlist = useCallback((guid) => {
        setActiveGuid(guid);
    }, []);

    return { ...state, switchWatchlist };
}

export function usePortfolioSidebarData() {
    const [state, setState] = useState({ items: null, id: null, loading: true, notFound: false, list: [] });
    const [activeId, setActiveId] = useState(null);

    useEffect(() => {
        let cancelled = false;
        async function load() {
            try {
                const listRes = await authorizedFetch(`${AUTH_BASE_URL}/api/personalize/portfolio/list`);
                const list = listRes?.data;
                if (!Array.isArray(list) || list.length === 0) {
                    if (!cancelled) setState({ items: null, loading: false, notFound: true, list: [] });
                    return;
                }
                const target = list.find((p) => p.id === activeId) || list.find((p) => p.isDefault) || list[0];
                const detailRes = await authorizedFetch(
                    `${AUTH_BASE_URL}/api/personalize/portfolio/detail/${target.id}`
                );
                const stocks = detailRes?.data?.stocks;
                const items = Array.isArray(stocks)
                    ? stocks.map((s) => ({
                        canonical_id: s.ticker,
                        symbol: s.ticker,
                        price: s.priceCloseIntraDay ?? null,
                        change_pct: s.pricePctChgIntraDay ?? null,
                        pnl_pct: s.returnValueChangePct ?? null,
                        report_count: 0,
                    }))
                    : [];
                if (!cancelled) setState({ items, id: target.id, name: target.name, loading: false, notFound: false, list });
            } catch {
                if (!cancelled) setState({ items: null, id: null, loading: false, notFound: true, list: [] });
            }
        }
        load();
        return () => { cancelled = true; };
    }, [activeId]);

    const switchPortfolio = useCallback((id) => {
        setActiveId(id);
    }, []);

    return { ...state, switchPortfolio };
}

export function useWatchlistFollow(symbol, type = 'stock') {
    const [watchlistId, setWatchlistId] = useState(null);
    const [isFollowed, setIsFollowed] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!symbol) { setLoading(false); return; }
        let cancelled = false;
        async function init() {
            try {
                const listRes = await authorizedFetch(`${AUTH_BASE_URL}/api/personalize/watchlist/list`);
                const list = listRes?.data;
                if (!Array.isArray(list) || list.length === 0) {
                    if (!cancelled) setLoading(false);
                    return;
                }
                const target = list.find((w) => w.isDefault) || list[0];
                const tickers = Array.isArray(target.tickers) ? target.tickers : [];
                if (!cancelled) {
                    setWatchlistId(target.id);
                    setIsFollowed(tickers.some((t) => t.toLowerCase() === symbol.toLowerCase()));
                    setLoading(false);
                }
            } catch {
                if (!cancelled) setLoading(false);
            }
        }
        init();
        return () => { cancelled = true; };
    }, [symbol]);

    const toggle = useCallback(async () => {
        if (!watchlistId || !symbol) return;
        try {
            if (isFollowed) {
                await authorizedFetch(`${AUTH_BASE_URL}/api/personalize/watchlist/delete-item`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ ticker: symbol, watchlistId }),
                });
                setIsFollowed(false);
            } else {
                await authorizedFetch(`${AUTH_BASE_URL}/api/personalize/watchlist/add-ticker`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ items: [{ ticker: symbol, type }], watchlistId }),
                });
                setIsFollowed(true);
            }
            window.dispatchEvent(new Event(WATCHLIST_UPDATED_EVENT));
        } catch { /* ignore */ }
    }, [watchlistId, symbol, type, isFollowed]);

    return { isFollowed, loading, toggle };
}
